
import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { AIUsageLog, Customer } from '../types';
import { api } from '../services/api';
import CreditTopUpModal from './CreditTopUpModal';

const Credits: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [logs, setLogs] = useState<AIUsageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomerForTopUp, setSelectedCustomerForTopUp] = useState<Customer | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [cData, lData] = await Promise.all([
          api.customers.getAll(),
          api.usage.getLogs()
        ]);
        setCustomers(Array.isArray(cData) ? cData : []);
        setLogs(Array.isArray(lData) ? lData : []);
      } catch (e) {
        console.error("Failed to fetch credit data", e);
        setCustomers([]);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleTopUp = async (customerId: string, amount: number) => {
    try {
      // Use specific API endpoint for credit management
      const updatedCustomer = await api.customers.manageCredits(customerId, amount, 'add');
      
      // Refresh local state with response
      setCustomers(prev => prev.map(c => c.id === customerId ? updatedCustomer : c));
    } catch (error) {
      console.error("Failed to top up credits", error);
      alert("Failed to add credits. Please try again.");
    }
    setSelectedCustomerForTopUp(null);
  };

  const usageData = [
    { day: 'Mon', usage: 4200 },
    { day: 'Tue', usage: 7500 },
    { day: 'Wed', usage: 5800 },
    { day: 'Thu', usage: 8200 },
    { day: 'Fri', usage: 9500 },
    { day: 'Sat', usage: 3100 },
    { day: 'Sun', usage: 2500 },
  ];

  // Defensive reduce calls
  const safeCustomers = Array.isArray(customers) ? customers : [];
  const totalAllocated = safeCustomers.reduce((sum, c) => sum + (c.credits?.totalAllocated || 0), 0);
  const totalUsed = safeCustomers.reduce((sum, c) => sum + (c.credits?.used || 0), 0);
  const utilization = totalAllocated > 0 ? (totalUsed / totalAllocated) * 100 : 0;

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center flex-col gap-4">
        <svg className="animate-spin h-12 w-12 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-slate-400 font-black uppercase tracking-widest text-xs">Loading User Wallets...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">User Credits</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Monitor and manage AI usage allowances for your customers.</p>
        </div>
        <div className="bg-indigo-50 dark:bg-indigo-900/30 px-4 py-2 rounded-xl border border-indigo-100 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-xs font-bold">
           Global Utilization: {utilization.toFixed(1)}%
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Credits Issued</p>
           <p className="text-3xl font-black text-slate-900 dark:text-white">{totalAllocated.toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Credits Consumed</p>
           <p className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{totalUsed.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 p-6 rounded-3xl shadow-lg shadow-indigo-200 dark:shadow-none text-white relative overflow-hidden">
           <div className="relative z-10">
             <p className="text-[10px] font-black text-indigo-200 uppercase tracking-widest mb-2">Top Spender</p>
             <p className="text-2xl font-black truncate">{safeCustomers.sort((a,b) => (b.credits?.used || 0) - (a.credits?.used || 0))[0]?.name || 'N/A'}</p>
             <p className="text-xs text-indigo-300 mt-1">
               {safeCustomers.sort((a,b) => (b.credits?.used || 0) - (a.credits?.used || 0))[0]?.credits?.used.toLocaleString() || 0} credits used
             </p>
           </div>
           <div className="absolute -right-4 -bottom-4 text-indigo-500/30">
              <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10H12V2Z"/><path d="M12 2a10 10 0 0 1 10 10h-7.5l-3-3L12 2Z"/><path d="M12 12V2.112A10 10 0 0 1 21.888 12H12Z"/></svg>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
             <h3 className="text-lg font-black text-slate-900 dark:text-white">Customer Wallets</h3>
             <button className="text-[10px] font-black text-slate-400 hover:text-indigo-600 uppercase tracking-widest transition-colors">View All</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Balance</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Usage</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Limit</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {safeCustomers.map(cust => {
                  const balance = cust.credits?.balance || 0;
                  const total = cust.credits?.totalAllocated || 100; // avoid div by zero
                  const percentage = Math.min(100, (cust.credits?.used || 0) / total * 100);
                  
                  return (
                    <tr key={cust.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-slate-900 dark:text-white">{cust.name}</span>
                          <span className="text-[10px] text-slate-400 font-mono">{cust.id}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{balance.toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                           <div className="flex-1 bg-slate-100 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${percentage > 80 ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${percentage}%` }}></div>
                           </div>
                           <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 w-8 text-right">{percentage.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{(cust.credits?.totalAllocated || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          onClick={() => setSelectedCustomerForTopUp(cust)}
                          className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-600 text-indigo-600 dark:text-indigo-400 transition-all shadow-sm"
                        >
                          Top Up
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-1 space-y-8">
           <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-6">Global Consumption</h3>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={usageData}>
                    <defs>
                      <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" opacity={0.3} />
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b'}} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                      formatter={(v: number) => [`${v}`, 'Credits']}
                    />
                    <Area type="monotone" dataKey="usage" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorUsage)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
           </div>

           <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-4">Recent System Logs</h3>
              <div className="space-y-4">
                 {logs.slice(0, 4).map((log, i) => (
                   <div key={i} className="flex justify-between items-start pb-4 border-b border-slate-50 dark:border-slate-700/50 last:border-0 last:pb-0">
                      <div>
                         <p className="text-xs font-bold text-slate-900 dark:text-white">{log.customerName || 'Unknown User'}</p>
                         <p className="text-[10px] text-slate-500 dark:text-slate-400">{log.task}</p>
                      </div>
                      <div className="text-right">
                         <span className="text-xs font-black text-slate-900 dark:text-white">-{log.creditsConsumed}</span>
                         <p className="text-[9px] text-slate-400">{log.model ? log.model.split(' ')[1] : 'AI'} Model</p>
                      </div>
                   </div>
                 ))}
              </div>
           </div>
        </div>
      </div>

      {selectedCustomerForTopUp && (
        <CreditTopUpModal 
          customer={selectedCustomerForTopUp} 
          onClose={() => setSelectedCustomerForTopUp(null)} 
          onConfirm={handleTopUp} 
        />
      )}
    </div>
  );
};

export default Credits;
